import { DropdownLayoutValueOption } from '@wix/design-system'
import { useContent, useMetadata, useNext } from '@wix/funnel-builder-lizard'
import { useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { onboardingPageView } from '../../../bi/onboarding'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { debounce } from '../../../utils/debounce'
import { BusinessTermService } from './api/BusinessTermService'
import { CategoryLayout } from './constants'
import { goNext, goSkip, loadCategories } from './state'
import type { Content } from './types'
import { useCategoryHandler } from './useCategoryHandler'

const debouncedLoadCategories = debounce(loadCategories, 400)

export type UseCategoryProps = ReturnType<typeof useCategory>

export const useCategory = (props: { businessTermService: BusinessTermService; layout?: CategoryLayout }) => {
  const { save, category: savedCategory } = useStorageContext()
  const { businessTermService, layout = CategoryLayout.NEW_CONCEPT } = props
  const [{ language }] = useMetadata()
  const next = useNext()

  const { content } = useContent<Content>()
  const { question, isAddUserInputAsOption } = content
  const { questionKey } = question

  const [searchParams] = useSearchParams()
  const prefilledInput = searchParams.get(questionKey) || searchParams.get('category') || ''

  const {
    state,
    dispatch,
    pageLogger,
    handlers: { handleClear, handleInputClick, handleSearchSelect, handleSearchChange, handleSuggestionClick },
    isLargeDesktopScreen,
    autoCompleteRef,
  } = useCategoryHandler({ savedCategory, questionKey, businessTermService, prefilledInput })

  const { search, searchSource, category, categories, clickedButton } = state

  const handleNextClick = useCallback(() => {
    if (clickedButton !== undefined) {
      return
    }
    dispatch({ type: 'buttonClicked', clickedButton: 'primary' })
    goNext({ category, pageLogger, language, questionKey, next, save })
  }, [dispatch, clickedButton, category, pageLogger, language, questionKey, next, save])

  const handleSkipClick = useCallback(() => {
    if (clickedButton !== undefined) {
      return
    }
    dispatch({ type: 'buttonClicked', clickedButton: 'skip' })
    goSkip({ pageLogger, next, questionKey, language, save })
  }, [dispatch, clickedButton, pageLogger, next, questionKey, language, save])

  const handleBackClick = useCallback(() => {
    if (clickedButton !== undefined) {
      return
    }
    dispatch({ type: 'buttonClicked', clickedButton: 'back' })
    void pageLogger.generalAction({ action: 'back' })
    window.history.back()
  }, [clickedButton, dispatch, pageLogger])

  useEffect(() => {
    void pageLogger.screenView({ initialData: prefilledInput })
  }, [pageLogger, prefilledInput])

  useEffect(() => {
    void debouncedLoadCategories({
      search,
      searchSource,
      dispatch,
      businessTermService,
      pageLogger,
      language,
      isAddUserInputAsOption,
    })
  }, [search, searchSource, dispatch, pageLogger, language, businessTermService, isAddUserInputAsOption])

  useEffect(() => {
    void onboardingPageView({ contentLanguage: language })
  }, [language])

  const categoriesOptions: DropdownLayoutValueOption[] = categories.map(category => ({
    id: category.displayNameOverride || category.displayName || '',
    value: category.displayNameOverride || category.displayName || '',
  }))

  const selectedId: string | number | undefined = useMemo(
    () =>
      (isAddUserInputAsOption && !category && categoriesOptions[0]?.id) || // this is required in order to keep the first option selected when user types something in search input
      category?.businessTerm?.displayNameOverride ||
      category?.businessTerm?.displayName ||
      '',
    [categoriesOptions, category, isAddUserInputAsOption],
  )

  return {
    handlers: {
      handleBackClick,
      handleClear,
      handleInputClick,
      handleSearchSelect,
      handleNextClick,
      handleSearchChange,
      handleSkipClick,
      handleSuggestionClick,
    },
    state,
    categoriesOptions,
    content,
    autoCompleteRef,
    selectedId,
    layout,
    isLargeDesktopScreen,
  }
}
